import React, { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { formatRelative, formatISOish } from "../../utils/formatters";
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import OutlinedInput from '@mui/material/OutlinedInput';
import Slider from '@mui/material/Slider';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import FilterListIcon from '@mui/icons-material/FilterList';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

// Helper function to apply min/max value filter
function applyValueFilter(rows, minValue, maxValue) {
  if (minValue === null && maxValue === null) return rows;
  
  return rows.filter(row => {
    const rowValue = parseFloat(row.value);
    if (isNaN(rowValue)) return true;
    
    if (minValue !== null && rowValue < minValue) return false;
    if (maxValue !== null && rowValue > maxValue) return false;
    return true;
  });
}

export default function DataTable({ 
  rows, 
  allRows,
  total, 
  page, 
  setPage, 
  pageSize,
  setPageSize,
  relativeTime, 
  setRelativeTime,
  showMeta,
  setShowMeta,
  showUnit,
  setShowUnit,
  loading,
  averageInterval,
  rawTotal,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [minValue, setMinValue] = useState(null);
  const [maxValue, setMaxValue] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [tempRange, setTempRange] = useState([0, 100]);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState(null); // 'asc' | 'desc' | null
  
  // Calculate min/max from data
  const dataRange = useMemo(() => {
    if (rows.length === 0) return [0, 100];
    let min = Infinity;
    let max = -Infinity;
    for (let i = 0; i < rows.length; i++) {
      const v = parseFloat(rows[i].value);
      if (!isNaN(v)) {
        if (v < min) min = v;
        if (v > max) max = v;
      }
    }
    if (min === Infinity) return [0, 100];
    return [min, max];
  }, [rows]);
  
  // Load filters from URL on mount
  useEffect(() => {
    const minParam = searchParams.get('min-value');
    const maxParam = searchParams.get('max-value');
    if (minParam) setMinValue(parseFloat(minParam));
    if (maxParam) setMaxValue(parseFloat(maxParam));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Apply client-side filters
  const filteredRows = useMemo(() => {
    return applyValueFilter(rows, minValue, maxValue);
  }, [rows, minValue, maxValue]);
  
  const handleOpenFilterDialog = (event) => {
    const currentMin = minValue !== null ? minValue : dataRange[0];
    const currentMax = maxValue !== null ? maxValue : dataRange[1];
    setTempRange([currentMin, currentMax]);
    setAnchorEl(event.currentTarget);
  };
  
  const handleClosePopover = () => {
    setAnchorEl(null);
  };
  
  const handleFilterSubmit = () => {
    const [newMin, newMax] = tempRange;
    
    // Only set if different from data range
    setMinValue(newMin !== dataRange[0] ? newMin : null);
    setMaxValue(newMax !== dataRange[1] ? newMax : null);
    setPage(0);
    
    // Update URL
    const params = new URLSearchParams(searchParams);
    if (newMin !== dataRange[0]) {
      params.set('min-value', newMin.toString());
    } else {
      params.delete('min-value');
    }
    if (newMax !== dataRange[1]) {
      params.set('max-value', newMax.toString());
    } else {
      params.delete('max-value');
    }
    setSearchParams(params, { replace: true });
    setAnchorEl(null);
  };
  
  const handleClearFilters = () => {
    setMinValue(null);
    setMaxValue(null);
    setTempRange(dataRange);
    setPage(0);
    
    const params = new URLSearchParams(searchParams);
    params.delete('min-value');
    params.delete('max-value');
    setSearchParams(params, { replace: true });
    setAnchorEl(null);
  };
  
  const isFiltered = minValue !== null || maxValue !== null;
  const isAveraged = averageInterval && averageInterval !== 'raw';
  const isSorted = sortColumn !== null;

  // Column accessor map for sorting
  const columnAccessor = {
    'Location': r => r.node || '',
    'Time': r => r.ts || '',
    'Variable': r => r.name || '',
    'Value': r => parseFloat(r.value) || 0,
    'Count': r => parseFloat(r.count) || 0,
    'Min': r => parseFloat(r.min) || 0,
    'Max': r => parseFloat(r.max) || 0,
    'Std': r => parseFloat(r.std) || 0,
    'Device': r => r.device || '',
  };

  const sortedRows = useMemo(() => {
    if (!sortColumn || !sortDirection) return filteredRows;
    const accessor = columnAccessor[sortColumn];
    if (!accessor) return filteredRows;
    const sorted = [...filteredRows].sort((a, b) => {
      const aVal = accessor(a);
      const bVal = accessor(b);
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return sorted;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRows, sortColumn, sortDirection]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortColumn(null);
        setSortDirection(null);
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setPage(0);
  };

  const pageCount = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const displayRows = sortedRows.slice(page * pageSize, (page + 1) * pageSize);
  
  const handleDownloadCSV = () => {
    const baseHeaders = [
      'Location', 
      'Time', 
      'Variable', 
      'Value',
      'Unit',
      'Device',
      'DeviceProfileName',
      'DeviceID',
      'SiteID',
      'TreeSpecies',
      'SensorName'
    ];
    
    const avgHeaders = isAveraged ? ['Count', 'Min', 'Max', 'Std'] : [];
    const allHeaders = [...baseHeaders, ...avgHeaders];
    
    // Build all rows first
    const allRowData = sortedRows.map(r => {
      const baseCols = [
        r.node || '',
        r.ts || '',
        r.name || '',
        r.value ?? '',
        r.unit || '',
        r.device || '',
        r.meta?.DeviceProfileName || '',
        r.sensorId || '',
        r.site || '',
        r.species || '',
        r.meta?.SensorName || ''
      ];
      const avgCols = isAveraged ? [
        r.count ?? '',
        r.min ?? '',
        r.max ?? '',
        r.std ?? ''
      ] : [];
      return [...baseCols, ...avgCols];
    });
    
    // Determine which columns have at least one non-empty value
    const keepCol = allHeaders.map((_, colIdx) =>
      allRowData.some(row => row[colIdx] !== '' && row[colIdx] !== null && row[colIdx] !== undefined)
    );
    
    const csvContent = [
      allHeaders.filter((_, i) => keepCol[i]).join(','),
      ...allRowData.map(row =>
        row.filter((_, i) => keepCol[i]).map(v => `"${v}"`).join(',')
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `crocus-data${isAveraged ? `-${averageInterval}-avg` : ''}-${new Date().toISOString()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };
  
  const handleResetSort = () => {
    setSortColumn(null);
    setSortDirection(null);
    setPage(0);
  };

  return (
    <div className="mt-4">
      {isSorted && (
        <Alert
          severity="info"
          className="mb-3"
          action={
            <Button onClick={handleResetSort} size="small">
              Reset
            </Button>
          }
        >
          Sorted by <strong>{sortColumn}</strong> ({sortDirection === 'asc' ? 'ascending' : 'descending'})
        </Alert>
      )}
      {isFiltered && (
        <Alert
          severity="info"
          className="mb-3"
          action={
            <Button onClick={handleClearFilters} size="small">
              Clear
            </Button>
          }
        >
          Data filtered from {rows.length.toLocaleString()} to {filteredRows.length.toLocaleString()} records
        </Alert>
      )}
      
      {isAveraged && (
        <Alert severity="info" className="mb-3">
          Showing {averageInterval} averages: {rawTotal.toLocaleString()} raw records → {filteredRows.length.toLocaleString()} averaged records
        </Alert>
      )}
      
      <div className="flex items-center justify-between mb-3 pb-3 border-b">
        <div className="flex items-center gap-4">
          <FormControlLabel
            control={
              <Checkbox 
                checked={relativeTime} 
                onChange={(e) => setRelativeTime(e.target.checked)}
                size="small"
              />
            }
            label={<span className="text-sm">relative time</span>}
          />
          <FormControlLabel
            control={
              <Checkbox 
                checked={showMeta} 
                onChange={(e) => setShowMeta(e.target.checked)}
                size="small"
              />
            }
            label={<span className="text-sm">meta</span>}
          />
          <FormControlLabel
            control={
              <Checkbox 
                checked={showUnit} 
                onChange={(e) => setShowUnit(e.target.checked)}
                size="small"
              />
            }
            label={<span className="text-sm">unit</span>}
          />
        </div>
        <div className="flex items-center gap-2">
          <Tooltip title="Filter values by range">
            <IconButton 
              size="small" 
              onClick={handleOpenFilterDialog}
              color={isFiltered ? "primary" : "default"}
            >
              <FilterListIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Download CSV">
            <IconButton size="small" onClick={handleDownloadCSV} disabled={!sortedRows.length}>
              <CloudDownloadOutlinedIcon />
            </IconButton>
          </Tooltip>
          
          <div className="h-6 w-px bg-gray-300 mx-2" />
          <FormControl variant="outlined" size="small" style={{ minWidth: 10 }}>
            <InputLabel id="rows-per-page-label" style={{ fontSize: '14px' }}>Rows</InputLabel>
            <Select
              labelId="rows-per-page-label"
              value={pageSize}
              onChange={(e) => {
                setPageSize(e.target.value);
                setPage(0);
              }}
              label="Rows"
              style={{ height: '32px', fontSize: '14px' }}
            >
              <MenuItem value={10} style={{ fontSize: '14px' }}>10</MenuItem>
              <MenuItem value={20} style={{ fontSize: '14px' }}>20</MenuItem>
              <MenuItem value={40} style={{ fontSize: '14px' }}>40</MenuItem>
              <MenuItem value={60} style={{ fontSize: '14px' }}>60</MenuItem>
              <MenuItem value={80} style={{ fontSize: '14px' }}>80</MenuItem>
              <MenuItem value={100} style={{ fontSize: '14px' }}>100</MenuItem>
            </Select>
          </FormControl>
          <div className="h-6 w-px bg-gray-300 mx-2" />          
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">
              {sortedRows.length ? 
                `${page * pageSize + 1}–${Math.min((page + 1) * pageSize, sortedRows.length)} of ${sortedRows.length}` 
                : "0 of 0"}
            </span>
            <Tooltip title="Previous page">
              <span>
                <IconButton 
                  size="small" 
                  disabled={page === 0}
                  onClick={() => setPage(Math.max(0, page - 1))}
                >
                  <KeyboardArrowLeftRoundedIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Next page">
              <span>
                <IconButton 
                  size="small" 
                  disabled={page >= pageCount - 1}
                  onClick={() => setPage(Math.min(pageCount - 1, page + 1))}
                >
                  <KeyboardArrowRightRoundedIcon />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              {[
                { label: 'Location', key: 'Location' },
                { label: 'Time', key: 'Time' },
                { label: 'Variable', key: 'Variable' },
                { label: 'Value', key: 'Value' },
                ...(isAveraged ? [
                  { label: 'Count', key: 'Count' },
                  { label: 'Min', key: 'Min' },
                  { label: 'Max', key: 'Max' },
                  { label: 'Std', key: 'Std' },
                ] : []),
                { label: 'Device', key: 'Device' },
                ...(showMeta ? [{ label: 'Meta', key: null }] : []),
              ].map(col => (
                <th
                  key={col.label}
                  className={`px-3 py-2 text-left ${col.key ? 'cursor-pointer select-none hover:bg-gray-100' : ''}`}
                  onClick={col.key ? () => handleSort(col.key) : undefined}
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    {col.key && sortColumn === col.key && (
                      sortDirection === 'asc'
                        ? <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                        : <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={5 + (isAveraged ? 4 : 0) + (showMeta ? 1 : 0)} className="px-3 py-6 text-center text-gray-500">Loading…</td>
              </tr>
            )}
            {!loading && displayRows.map((r, i) => {
              const valueDisplay = showUnit && r.unit ? `${r.value} ${r.unit}` : (r.value ?? "-");
              
              return (
                <tr key={i} className="border-b hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <a 
                      className="text-blue-600 underline" 
                      href={`/location/${r.node}`}
                    >
                      {r.node || "-"}
                    </a>
                  </td>
                  <td className="px-3 py-2">
                    {relativeTime ? formatRelative(r.ts) : formatISOish(r.ts)}
                  </td>
                  <td className="px-3 py-2">
                    <a 
                      className="text-blue-600 underline" 
                      href={`/data/ontology/${r.name}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {r.name || "-"}
                    </a>
                  </td>
                  <td className="px-3 py-2">{valueDisplay}</td>
                  {isAveraged && <td className="px-3 py-2">{r.count ?? "-"}</td>}
                  {isAveraged && <td className="px-3 py-2">{r.min ?? "-"}</td>}
                  {isAveraged && <td className="px-3 py-2">{r.max ?? "-"}</td>}
                  {isAveraged && <td className="px-3 py-2">{r.std ?? "-"}</td>}
                  <td className="px-3 py-2">{r.device || "-"}</td>
                  {showMeta && (
                    <td className="px-3 py-2 text-xs">
                      {r.meta && Object.entries(r.meta).map(([key, value]) => (
                        <div key={key}>
                          <b>{key}</b>: {value}
                        </div>
                      ))}
                    </td>
                  )}
                </tr>
              );
            })}
            {!loading && !displayRows.length && (
              <tr>
                <td colSpan={5 + (isAveraged ? 4 : 0) + (showMeta ? 1 : 0)} className="px-3 py-6 text-center text-gray-500">
                  {rows.length > 0 && isFiltered 
                    ? "No data matches the applied filters."
                    : "No data. Run a query to see results."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Filter Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Typography sx={{ p: 2 }}>
          <Box sx={{ width: 600 }}>
            <Typography gutterBottom>value</Typography>
            
            <div className="flex items-center gap-4">
              <OutlinedInput
                size="small"
                type="number"
                value={tempRange[0]}
                onChange={(e) => setTempRange([parseFloat(e.target.value) || dataRange[0], tempRange[1]])}
                inputProps={{
                  step: (dataRange[1] - dataRange[0]) / 1000,
                  min: dataRange[0],
                  max: dataRange[1],
                }}
              />
              <Slider
                sx={{ width: 400 }}
                value={tempRange}
                onChange={(e, newValue) => setTempRange(newValue)}
                min={dataRange[0]}
                max={dataRange[1]}
                step={(dataRange[1] - dataRange[0]) / 1000}
                valueLabelDisplay="auto"
              />
              <OutlinedInput
                size="small"
                type="number"
                value={tempRange[1]}
                onChange={(e) => setTempRange([tempRange[0], parseFloat(e.target.value) || dataRange[1]])}
                inputProps={{
                  step: (dataRange[1] - dataRange[0]) / 1000,
                  min: dataRange[0],
                  max: dataRange[1],
                }}
              />
            </div>
          </Box>
          
          <div className="flex justify-between mt-4">
            <Button onClick={handleClearFilters}>
              Clear Filters
            </Button>
            <Button onClick={handleFilterSubmit} variant="contained">
              Close
            </Button>
          </div>
        </Typography>
      </Popover>
    </div>
  );
}